import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const formatMoney = (value, element) => new Intl.NumberFormat(element.dataset.locale || document.documentElement.lang || 'en', { style: 'currency', currency: element.dataset.currency || 'EUR' }).format(value / 100);

async function addItems(element, items) {
  const deferred = CartLinesUpdateEvent.createPromise();
  element.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: element.closest('dialog') ? 'dialog' : 'product', lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
  const root = window.Shopify?.routes?.root || '/';
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(component => component.dataset.sectionId))].slice(0, 5);
  try {
    const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: window.location.pathname }) });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || element.dataset.error);
    let cart;
    try {
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('cart-refresh');
      cart = await cartResponse.json();
    } catch {
      deferred.resolve({ detail: { didError: false, sections: result.sections }, cart: { id: '', totalQuantity: result.items.reduce((sum, item) => sum + item.quantity, 0), cost: { totalAmount: { amount: '0', currencyCode: element.dataset.currency || 'EUR' } }, lines: [] } });
      window.location.assign(`${root}cart`);
      return;
    }
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: false, sections: result.sections, source: 'limited-one-native', items: cart.items, itemCount: cart.item_count } });
    if (!document.querySelector('#cart-drawer')) window.location.assign(`${root}cart`);
    return cart;
  } catch (error) {
    deferred.resolve({ detail: { didError: true } });
    throw error;
  }
}

class LoProductSet extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.updateTotal();
  }

  updateTotal() {
    let total = 0;
    for (const select of this.querySelectorAll('select')) {
      const option = select.selectedOptions[0];
      const amount = Number(option?.dataset.price || 0);
      total += amount;
      const price = select.closest('.lo-set__row')?.querySelector('[data-lo-item-price]');
      if (price) price.textContent = formatMoney(amount, this);
    }
    if (this.refs.total) this.refs.total.textContent = formatMoney(total, this);
  }

  async addSet(event) {
    event.preventDefault();
    if (this.busy || this.refs.button?.disabled) return;
    const items = [...this.querySelectorAll('select')].map(select => ({ id: Number(select.value), quantity: 1 }));
    if (items.length !== 3 || items.some(item => !item.id)) return;
    this.busy = true;
    const button = this.refs.button;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.adding;
    this.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = '';
    try {
      await addItems(this, items);
      this.refs.status.textContent = this.dataset.added;
    } catch (error) {
      this.refs.status.textContent = error.message || this.dataset.error;
    } finally {
      this.busy = false;
      button.disabled = false;
      button.textContent = label;
      this.removeAttribute('aria-busy');
    }
  }
}

class LoQuickOffer extends Component {
  async addOffer(event) {
    event.preventDefault();
    if (this.busy) return;
    const select = this.querySelector('[name="id"]');
    if (!select || !Number(select.value)) return;
    this.busy = true;
    const label = this.refs.button.textContent;
    this.refs.button.disabled = true;
    this.refs.status.textContent = '';
    this.refs.button.textContent = this.dataset.adding;
    try {
      await addItems(this, [{ id: Number(select.value), quantity: 1 }]);
      this.refs.status.textContent = this.dataset.added;
    } catch (error) {
      this.refs.status.textContent = error.message || this.dataset.error;
    } finally {
      this.busy = false;
      this.refs.button.disabled = false;
      this.refs.button.textContent = label;
    }
  }

  updatePrice() {
    const select = this.querySelector('select');
    const amount = Number(select?.selectedOptions[0]?.dataset.price || 0);
    if (this.refs.price) this.refs.price.textContent = formatMoney(amount, this);
  }
}

class LoBagControl extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.listener = event => event.promise?.then(({ cart }) => {
      if (!cart) return;
      if (this.refs.count) this.refs.count.textContent = cart.totalQuantity;
      if (this.refs.link) this.refs.link.setAttribute('aria-label', `${this.dataset.label} (${cart.totalQuantity})`);
    }).catch(() => {});
    document.addEventListener(StandardEvents.cartLinesUpdate, this.listener);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.listener);
    super.disconnectedCallback();
  }

  openBag(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.querySelector('#cart-drawer');
    if (typeof drawer?.open === 'function') {
      event.preventDefault();
      drawer.open();
    }
  }
}

class LoBackLink extends Component {
  goBack(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    try {
      if (document.referrer && new URL(document.referrer).origin === window.location.origin && window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    } catch {}
  }
}

class LoGuideControl extends Component {
  openGuide() {
    const guide = document.querySelector('#lo-room-guide');
    if (typeof guide?.open === 'function') guide.open();
  }
}

for (const [tag, component] of [['lo-product-set', LoProductSet], ['lo-quick-offer', LoQuickOffer], ['lo-bag-control', LoBagControl], ['lo-back-link', LoBackLink], ['lo-guide-control', LoGuideControl]]) {
  if (!customElements.get(tag)) customElements.define(tag, component);
}
